import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, LinearProgress, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getCameraUploadBatch, CameraUploadBatch } from '../../api/cameraUpload';

interface BatchProgressProps {
  deviceId: string;
  batchId: string;
  onSettled: () => void;
}

const POLL_INTERVAL_MS = 2000;

const BatchProgress: React.FC<BatchProgressProps> = ({ deviceId, batchId, onSettled }) => {
  const auth = useAuth();
  const [batch, setBatch] = useState<CameraUploadBatch | null>(null);
  const [error, setError] = useState('');
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    let cancelled = false;
    const accessToken = auth.tokens?.accessToken ?? '';

    const poll = async () => {
      try {
        const result = await getCameraUploadBatch(deviceId, batchId, accessToken);
        if (cancelled) return;
        setBatch(result);
        if (result.status === 'Completed' || result.status === 'Failed') {
          onSettledRef.current();
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load batch status');
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, batchId]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!batch) return null;

  const percent = batch.totalImages > 0 ? Math.round((batch.processedImages / batch.totalImages) * 100) : 0;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {batch.status === 'Failed' ? 'Upload failed' : `Processing ${batch.originalFileName}…`}
      </Typography>
      {batch.status !== 'Failed' && (
        <>
          <LinearProgress variant="determinate" value={percent} sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {batch.processedImages} / {batch.totalImages} images processed
          </Typography>
        </>
      )}
      {batch.status === 'Failed' && batch.errorSummary && (
        <Alert severity="error" sx={{ mt: 1 }}>{batch.errorSummary}</Alert>
      )}
    </Paper>
  );
};

export default BatchProgress;
